import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtUSD, num } from "@/lib/format";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft } from "lucide-react";

const ROLES = ["Nursing student", "New graduate RN", "Staff RN", "PRN RN", "Travel nurse", "Experienced RN", "Advanced practice nurse"];
const SPECIALTIES = ["Medical-Surgical", "Telemetry", "ICU", "CVICU", "Emergency Department", "Oncology", "Dialysis", "Hospice", "Home Health", "Operating Room", "PACU", "Labor & Delivery", "Pediatrics", "Case Management", "Occupational Health", "Other"];
const GOALS = [100000, 125000, 150000, 175000, 200000];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    nursing_role: "", years_experience: "", specialty: "",
    hourly_rate: "", weekly_hours: "", current_annual_income: "", income_goal: "",
  });
  const [customGoal, setCustomGoal] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const autoAnnual = num(f.hourly_rate) * num(f.weekly_hours) * 52;

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/profile", {
        nursing_role: f.nursing_role || null,
        years_experience: f.years_experience ? num(f.years_experience) : null,
        specialty: f.specialty || null,
        hourly_rate: f.hourly_rate ? num(f.hourly_rate) : null,
        weekly_hours: f.weekly_hours ? num(f.weekly_hours) : null,
        current_annual_income: f.current_annual_income ? num(f.current_annual_income) : (autoAnnual || null),
        income_goal: f.income_goal ? num(f.income_goal) : null,
        onboarded: true,
      });
      await refreshUser();
      toast.success("You're all set!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8">
      <div className="mx-auto max-w-lg">
        <div className="flex justify-center"><Logo /></div>
        <div className="mt-6 flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-[#18A6A6]" : "bg-slate-200"}`} />
          ))}
        </div>

        <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h1 className="font-heading text-2xl font-bold text-[#0F2747]">What best describes you?</h1>
                <p className="mt-1 text-sm text-slate-500">Welcome{user?.first_name ? `, ${user.first_name}` : ""}! Let's personalize your dashboard.</p>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                {ROLES.map((r) => (
                  <button key={r} type="button" data-testid={`role-${r.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => set("nursing_role", r)}
                    className={`rounded-xl border p-3 text-left text-sm font-medium transition-all ${
                      f.nursing_role === r ? "border-[#18A6A6] bg-[#EAF4FA] text-[#0F2747]" : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}>
                    {r}
                  </button>
                ))}
              </div>
              <div>
                <Label htmlFor="years">Years of nursing experience</Label>
                <Input id="years" type="number" inputMode="decimal" data-testid="onboarding-years-input"
                  value={f.years_experience} onChange={(e) => set("years_experience", e.target.value)}
                  className="mt-1.5 rounded-xl" placeholder="e.g. 3" />
              </div>
              <div>
                <Label>Primary specialty</Label>
                <Select value={f.specialty} onValueChange={(v) => set("specialty", v)}>
                  <SelectTrigger data-testid="onboarding-specialty-select" className="mt-1.5 rounded-xl">
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="onboarding-next-1" onClick={() => setStep(2)} disabled={!f.nursing_role}
                className="w-full rounded-full bg-[#18A6A6] py-6 hover:bg-[#148f8f]">
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h1 className="font-heading text-2xl font-bold text-[#0F2747]">Your current pay</h1>
              <div>
                <Label htmlFor="rate">Current hourly rate ($)</Label>
                <Input id="rate" type="number" inputMode="decimal" data-testid="onboarding-rate-input"
                  value={f.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)}
                  className="mt-1.5 rounded-xl" placeholder="e.g. 42" />
              </div>
              <div>
                <Label htmlFor="hours">Average scheduled hours per week</Label>
                <Input id="hours" type="number" inputMode="decimal" data-testid="onboarding-hours-input"
                  value={f.weekly_hours} onChange={(e) => set("weekly_hours", e.target.value)}
                  className="mt-1.5 rounded-xl" placeholder="e.g. 36" />
              </div>
              {autoAnnual > 0 && (
                <div className="rounded-2xl bg-[#EAF4FA] p-4">
                  <p className="text-xs font-medium text-slate-500">Estimated annual gross income</p>
                  <p className="font-heading text-2xl font-bold text-[#18A6A6]">{fmtUSD(autoAnnual)}</p>
                </div>
              )}
              <div>
                <Label htmlFor="annual">Current estimated annual income ($)</Label>
                <Input id="annual" type="number" inputMode="decimal" data-testid="onboarding-annual-input"
                  value={f.current_annual_income} onChange={(e) => set("current_annual_income", e.target.value)}
                  className="mt-1.5 rounded-xl" placeholder={autoAnnual ? String(Math.round(autoAnnual)) : "Auto-calculated"} />
                <p className="mt-1 text-xs text-slate-400">Leave blank to use the auto-calculated amount above.</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" data-testid="onboarding-back-2" onClick={() => setStep(1)} className="rounded-full py-6">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button data-testid="onboarding-next-2" onClick={() => setStep(3)} className="flex-1 rounded-full bg-[#18A6A6] py-6 hover:bg-[#148f8f]">
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h1 className="font-heading text-2xl font-bold text-[#0F2747]">What income would you like to reach?</h1>
              {!customGoal ? (
                <div className="grid grid-cols-2 gap-2.5">
                  {GOALS.map((g) => (
                    <button key={g} type="button" data-testid={`goal-${g}`} onClick={() => set("income_goal", String(g))}
                      className={`rounded-xl border p-4 text-center font-heading text-lg font-bold transition-all ${
                        num(f.income_goal) === g ? "border-[#18A6A6] bg-[#EAF4FA] text-[#0F2747]" : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}>
                      {g === 200000 ? "$200,000+" : fmtUSD(g)}
                    </button>
                  ))}
                  <button type="button" data-testid="goal-custom" onClick={() => { setCustomGoal(true); set("income_goal", ""); }}
                    className="col-span-2 rounded-xl border border-dashed border-slate-300 p-3 text-sm font-medium text-slate-500 hover:border-[#18A6A6]">
                    Enter a custom goal
                  </button>
                </div>
              ) : (
                <div>
                  <Label htmlFor="customGoal">Custom annual income goal ($)</Label>
                  <Input id="customGoal" type="number" inputMode="decimal" data-testid="onboarding-custom-goal-input"
                    value={f.income_goal} onChange={(e) => set("income_goal", e.target.value)}
                    className="mt-1.5 rounded-xl" placeholder="e.g. 135000" autoFocus />
                  <button type="button" onClick={() => setCustomGoal(false)} className="mt-2 text-xs font-medium text-[#18A6A6]">
                    ← Pick from presets
                  </button>
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" data-testid="onboarding-back-3" onClick={() => setStep(2)} className="rounded-full py-6">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Button data-testid="onboarding-finish-btn" onClick={finish} disabled={saving || !f.income_goal}
                  className="flex-1 rounded-full bg-[#0F2747] py-6 hover:bg-[#16355F]">
                  {saving ? "Setting up..." : "Go to my dashboard"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
